/* PRODUCTION STATUS (PSR) — Jay, 01/Nov/2026: vendors log in and update progress on rows the Portal builds from the Master Sheet,
   so PT stops collecting a different Excel file from each vendor every week. One screen for both sides:
   - a vendor (TS, MS) sees its own company's orders only — the server decides that from the login — with this week's to-do
     first (actual dates missing, HEVN changes to acknowledge, orders without a Confirmed EXF);
   - HEVN sees every vendor; PT (production_status edit) updates rows, runs the Master sync and decides dates vendors suggest.
   Rows are production lots (psr.js): one row per vendor order + item + colour + frame + EXF, saved to every PO in it.
   Dates read dd/Mon/yyyy (spec). No price anywhere. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ListChecks, BellRing, RefreshCw, X, Check, CalendarClock, CalendarDays, Download, Upload } from 'lucide-react';
import Header from './Header';
import useAuth from './useAuth';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';
import ModuleHelp from './ModuleHelp';
import './style.css';
import './ProjectApproval.css';
import './SPHub.css';
import './ProductionStatus.css';

const API = `${BACKEND_URL}/api/psr`;
const errOf = (e) => e?.response?.data?.error || e.message;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dmy = (iso) => (iso ? `${iso.slice(8, 10)}/${MON[Number(iso.slice(5, 7)) - 1]}/${iso.slice(0, 4)}` : '');
const dmyTime = (d) => { if (!d) return ''; const x = new Date(new Date(d).getTime() + 7 * 3600000).toISOString(); return `${dmy(x.slice(0, 10))} ${x.slice(11, 16)}`; };
const STATUS_ORDER = ['late_vendor', 'late_dac', 'actual_missing', 'no_input', 'on_track', 'shipped', 'closed', 'cancelled'];
const STEP_STATUS = ['planned', 'done', 'na', 'co'];
/* THE GUIDE (Max 01/11: every module has one). A vendor and HEVN get different screens; a step whose control is not there —
   the to-do strip for HEVN, the sync button for anybody but PT — is skipped by TourGuide. */
const TOUR = [
  { selector: '[data-psr-todo]', title: 'xPsr.tourTodoT', body: 'xPsr.tourTodoB' },
  { selector: '[data-psr-search]', title: 'xPsr.tourFindT', body: 'xPsr.tourFindB' },
  { selector: '[data-psr-open]', title: 'xPsr.tourOpenT', body: 'xPsr.tourOpenB' },
  { selector: '.psr-table thead .psr-sel', title: 'xPsr.tourBulkT', body: 'xPsr.tourBulkB' },
  { selector: '[data-psr-xldown]', title: 'xPsr.tourExcelT', body: 'xPsr.tourExcelB' },
  { selector: '[data-psr-tab="cal"]', title: 'xPsr.tourCalT', body: 'xPsr.tourCalB' },
  { selector: '[data-psr-tab="changes"]', title: 'xPsr.tourChangesT', body: 'xPsr.tourChangesB' },
  { selector: '[data-psr-sync]', title: 'xPsr.tourSyncT', body: 'xPsr.tourSyncB' },
];
// a history field as people read it: 'step:cutting.actual' → "Cutting · Actual", 'cfm_exf' → "Confirmed EXF"; a value as dd/Mon/yyyy or a step status word
const STEP_PART = { plan: 'stepPlan', actual: 'stepActual', status: 'stepStatus', coRef: 'stepCoRef', qty: 'colQty' };
const LOT_FIELD = { cfm_exf: 'cfmExf', remark: 'remark', your_order_no: 'yourOrderNo' };
const psrField = (t, f) => {
  if (!f.startsWith('step:')) return t(`xPsr.${LOT_FIELD[f] || `fld_${f}`}`, { defaultValue: f });
  const [step, part] = f.slice(5).split('.');
  return `${t(`xPsr.step_${step}`)} · ${STEP_PART[part] ? t(`xPsr.${STEP_PART[part]}`) : part}`;
};
const psrValue = (t, v) => {
  if (v == null || v === '') return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return dmy(v);
  return STEP_STATUS.includes(v) ? t(`xPsr.ss_${v}`) : v;
};

function Chip({ status }) {
  const { t } = useTranslation();
  return <span className={`psr-chip ${status}`}>{t(`xPsr.st_${status}`)}</span>;
}

/* one lot: its Master lines, the vendor's fields, the 12 steps, HEVN changes to answer, and the history */
function LotDialog({ id, me, steps, onClose, onSaved }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [draft, setDraft] = useState({});        // { cfmExf, yourOrderNo, remark, steps: { key: {…} } }
  const [reason, setReason] = useState('');
  const [needReason, setNeedReason] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [suggest, setSuggest] = useState({});    // event id → date
  const today = useMemo(() => new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 10), []);
  const load = useCallback(() => axios.get(`${API}/lots/${id}`).then((r) => { setD(r.data); setDraft({}); }).catch((e) => setMsg({ bad: true, text: errOf(e) })), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  if (!d) return null;
  const lot = d.lot;
  const canEdit = me.canEdit && !['closed', 'cancelled'].includes(lot.state);
  const val = (k) => (k in draft ? draft[k] : lot[k]);
  const stepVal = (k, f) => {
    const dr = draft.steps?.[k];
    if (dr && f in dr) return dr[f];
    const s = lot.steps[k] || {};
    return f === 'status' ? (s.status || 'planned') : (s[f] ?? '');
  };
  const setStep = (k, f, v) => setDraft((p) => ({ ...p, steps: { ...(p.steps || {}), [k]: { ...(p.steps?.[k] || {}), [f]: v } } }));
  const dirty = Object.keys(draft).length > 0;
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await axios.put(`${API}/lots/${id}`, { ...draft, reason });
      setMsg({ bad: false, text: t('xPsr.saved') }); setNeedReason(false); setReason('');
      await load(); onSaved();
    } catch (e) {
      const text = errOf(e);
      if (/weekly close/i.test(text)) setNeedReason(true);
      setMsg({ bad: true, text: /weekly close/i.test(text) ? t('xPsr.reasonNeeded') : text });
    } finally { setBusy(false); }
  };
  const ack = async (ev, action) => {
    try {
      await axios.post(`${API}/events/${ev.id}/ack`, action === 'suggest' ? { action, value: suggest[ev.id] } : { action });
      await load(); onSaved();
    } catch (e) { setMsg({ bad: true, text: errOf(e) }); }
  };
  const decide = async (ev, decision) => {
    try { await axios.post(`${API}/events/${ev.id}/decide`, { decision }); await load(); onSaved(); }
    catch (e) { setMsg({ bad: true, text: errOf(e) }); }
  };
  const pending = d.events.filter((e) => e.kind === 'hevn' && ['pending', 'suggested'].includes(e.ackStatus));
  const fieldLabel = (f) => psrField(t, f);
  const showVal = (v) => psrValue(t, v);

  return ReactDOM.createPortal(
    <div className="psr-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="psr-dialog" role="dialog" aria-modal="true" aria-labelledby="psr-dlg-title" data-psr-dialog={lot.id}>
        <div className="psr-dlg-head">
          <div>
            <h3 id="psr-dlg-title">{lot.description} <span className="psr-muted">· {lot.color}{lot.frame ? ` · ${lot.frame}` : ''}</span></h3>
            <div className="psr-muted">{lot.lotNo} · {lot.vendor} · {lot.vendorOrderNo || '—'} · {t('xPsr.colQty')} {Number(lot.reqQty).toLocaleString()} · {t('xPsr.colReq')} {dmy(lot.reqExf)}</div>
          </div>
          <Chip status={lot.status} />
          <button type="button" className="psr-x" onClick={onClose} aria-label={t('xPsr.close')}><X size={18} /></button>
        </div>
        <div className="psr-dlg-body">
          {msg && <p className={msg.bad ? 'sp-error' : 'sp-ok'} role="status">{msg.text}</p>}
          {pending.length > 0 && (
            <section className="psr-hevn" data-psr-hevn>
              <h4><BellRing size={15} /> {t('xPsr.hevnChanges')}</h4>
              {pending.map((ev) => (
                <div key={ev.id} className="psr-hevn-row" data-psr-event={ev.id}>
                  <span><b>{fieldLabel(ev.field)}</b>: {showVal(ev.oldValue)} → <b>{showVal(ev.newValue)}</b> <span className="psr-muted">{dmyTime(ev.createdAt)}</span></span>
                  {ev.ackStatus === 'suggested' ? (
                    <span className="psr-hevn-act">
                      <em>{t('xPsr.suggested', { d: dmy(ev.suggestValue) })}</em>
                      {me.pt && <><button type="button" className="sp-btn primary" onClick={() => decide(ev, 'accept')} data-psr-accept={ev.id}><Check size={13} /> {t('xPsr.accept')}</button>
                        <button type="button" className="sp-btn" onClick={() => decide(ev, 'reject')} data-psr-reject={ev.id}>{t('xPsr.reject')}</button></>}
                    </span>
                  ) : me.canEdit && (
                    <span className="psr-hevn-act">
                      <button type="button" className="sp-btn primary" onClick={() => ack(ev, 'acknowledge')} data-psr-ack={ev.id}><Check size={13} /> {t('xPsr.acknowledge')}</button>
                      {ev.field === 'req_exf' && (
                        <>
                          <input type="date" value={suggest[ev.id] || ''} onChange={(e) => setSuggest((p) => ({ ...p, [ev.id]: e.target.value }))} aria-label={t('xPsr.suggest')} />
                          <button type="button" className="sp-btn" disabled={!suggest[ev.id]} onClick={() => ack(ev, 'suggest')} data-psr-suggest={ev.id}>{t('xPsr.suggest')}</button>
                        </>
                      )}
                    </span>
                  )}
                </div>
              ))}
            </section>
          )}

          <section className="psr-grid2">
            <label className="psr-fld"><span>{t('xPsr.cfmExf')}</span>
              <div className="psr-inline">
                <input type="date" value={val('cfmExf') || ''} disabled={!canEdit} onChange={(e) => setDraft((p) => ({ ...p, cfmExf: e.target.value }))} data-psr-cfm />
                {canEdit && <button type="button" className="sp-btn" onClick={() => setDraft((p) => ({ ...p, cfmExf: lot.reqExf }))}>{t('xPsr.cfmSame')}</button>}
                {!lot.cfmByVendor && !('cfmExf' in draft) && <em className="psr-warn">{t('xPsr.cfmNotYet')}</em>}
              </div>
            </label>
            <label className="psr-fld"><span>{t('xPsr.yourOrderNo')}</span>
              <input value={val('yourOrderNo') || ''} disabled={!canEdit} maxLength={60} onChange={(e) => setDraft((p) => ({ ...p, yourOrderNo: e.target.value }))} />
            </label>
            <label className="psr-fld psr-wide"><span>{t('xPsr.remark')}</span>
              <textarea rows={2} value={val('remark') || ''} disabled={!canEdit} maxLength={1000} onChange={(e) => setDraft((p) => ({ ...p, remark: e.target.value }))} data-psr-remark />
            </label>
          </section>

          <h4>{t('xPsr.steps')}</h4>
          <div className="psr-steps-wrap">
            <table className="sp-table psr-steps">
              <thead><tr><th>{t('xPsr.bulkStep')}</th><th>{t('xPsr.stepPlan')}</th><th>{t('xPsr.stepActual')}</th><th>{t('xPsr.stepStatus')}</th><th>{t('xPsr.stepCoRef')}</th><th>{t('xPsr.colQty')}</th></tr></thead>
              <tbody>
                {steps.map(({ key }) => {
                  const locked = !canEdit || (key === 'frame' && me.isVendor);
                  const st = stepVal(key, 'status');
                  return (
                    <tr key={key} className={st === 'na' ? 'na' : ''} data-psr-step={key}>
                      <td className="psr-nowrap"><b>{t(`xPsr.step_${key}`)}</b>{key === 'frame' && <div className="psr-muted psr-small">{t('xPsr.frameDac')}</div>}</td>
                      <td><input type="date" value={stepVal(key, 'plan') || ''} disabled={locked} onChange={(e) => setStep(key, 'plan', e.target.value)} data-psr-plan={key} /></td>
                      <td className="psr-nowrap">
                        <input type="date" max={today} value={stepVal(key, 'actual') || ''} disabled={locked} onChange={(e) => setStep(key, 'actual', e.target.value)} data-psr-actual={key} />
                        {!locked && <button type="button" className="sp-btn psr-today" onClick={() => setStep(key, 'actual', today)} data-psr-today={key}>{t('xPsr.today')}</button>}
                      </td>
                      <td>
                        <select value={st} disabled={locked} onChange={(e) => setStep(key, 'status', e.target.value)} data-psr-status={key}>
                          {STEP_STATUS.map((s) => <option key={s} value={s}>{t(`xPsr.ss_${s}`)}</option>)}
                        </select>
                      </td>
                      <td><input value={stepVal(key, 'coRef') || ''} disabled={locked || st !== 'co'} maxLength={60} onChange={(e) => setStep(key, 'coRef', e.target.value)} /></td>
                      <td><input type="number" min="0" className="psr-qty" value={stepVal(key, 'qty') ?? ''} disabled={locked} onChange={(e) => setStep(key, 'qty', e.target.value)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h4>{t('xPsr.lines')}</h4>
          <table className="sp-table psr-lines">
            <thead><tr><th>{t('xPsr.linePo')}</th>{!me.isVendor && <><th>{t('xPsr.lineCustomerPo')}</th><th>{t('xPsr.lineDest')}</th></>}<th className="num">{t('xPsr.colQty')}</th><th>{t('xPsr.colReq')}</th></tr></thead>
            <tbody>
              {d.lines.map((l) => (
                <tr key={l.rowKey} className={l.goneAt ? 'gone' : ''}>
                  <td>{l.po || '—'}</td>{!me.isVendor && <><td>{l.customerPo || '—'}</td><td>{l.destination || '—'}</td></>}
                  <td className="num">{Number(l.qty).toLocaleString()}</td><td>{dmy(l.reqExf)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <details className="psr-history">
            <summary>{t('xPsr.history')} ({d.events.length})</summary>
            {!d.events.length ? <p className="psr-muted">{t('xPsr.noHistory')}</p> : (
              <ul>
                {d.events.map((e) => (
                  <li key={e.id}>
                    <span className="psr-muted">{dmyTime(e.createdAt)}</span> · <b>{e.userName || e.kind}</b> · {fieldLabel(e.field)}: {showVal(e.oldValue)} → {showVal(e.newValue)}
                    {e.via && <span className="psr-muted"> ({e.via})</span>}
                    {e.afterClose ? <em className="psr-warn"> · {t('xPsr.afterClose')}</em> : null}
                    {e.reason ? <span className="psr-muted"> — {e.reason}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </details>
        </div>
        <div className="psr-dlg-foot">
          {needReason && <input className="psr-reason" placeholder={t('xPsr.reason')} value={reason} onChange={(e) => setReason(e.target.value)} data-psr-reason />}
          <button type="button" className="sp-btn" onClick={onClose}>{t('xPsr.close')}</button>
          {canEdit && <button type="button" className="sp-btn primary" disabled={!dirty || busy || (needReason && !reason.trim())} onClick={save} data-psr-save>{busy ? t('xPsr.saving') : t('xPsr.save')}</button>}
        </div>
      </div>
    </div>, document.body,
  );
}

/* the uploaded file, before anything is written: each row's changes and problems; Apply writes the rows without problems */
function ImportPreview({ data, onApply, onClose, busy }) {
  const { t } = useTranslation();
  return ReactDOM.createPortal(
    <div className="psr-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="psr-dialog" role="dialog" aria-modal="true" aria-labelledby="psr-xl-title" data-psr-xlpreview>
        <div className="psr-dlg-head"><div><h3 id="psr-xl-title">{data.applied ? t('xPsr.xlApplied', data) : t('xPsr.xlPreview')}</h3>
          <div className="psr-muted">{t('xPsr.xlSummary', data)}</div></div>
          <button type="button" className="psr-x" onClick={onClose} aria-label={t('xPsr.close')}><X size={18} /></button></div>
        <div className="psr-dlg-body">
          <table className="sp-table">
            <thead><tr><th>{t('xPsr.xlRow')}</th><th>{t('xPsr.xlRowId')}</th><th>{t('xPsr.tabChanges')}</th></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.row} className={r.ok ? '' : 'psr-bad'} data-psr-xlrow={r.lotNo}>
                  <td>{r.row}</td><td className="psr-nowrap">{r.lotNo}</td>
                  <td>{r.ok ? (r.changes || []).map((c) => `${psrField(t, c.field)}: ${psrValue(t, c.old)} → ${psrValue(t, c.new)}`).join(' · ') || `${r.changed} ✓` : <span className="sp-error">{r.error}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="psr-dlg-foot">
          <button type="button" className="sp-btn" onClick={onClose}>{t('xPsr.close')}</button>
          {!data.applied && data.ok > 0 && <button type="button" className="sp-btn primary" disabled={busy} onClick={onApply} data-psr-xlapply>{t('xPsr.xlApply')}</button>}
        </div>
      </div>
    </div>, document.body,
  );
}

/* THE SHIP CALENDAR (Jay's spec, 01/11): HEVN's read-only month view of the shipment plan, laid out like Google Calendar's —
   weeks from Sunday with their week number on the left, the vendors picked by checkbox, each row on its Required EXF or, when
   asked, its Confirmed EXF (a row nobody has confirmed stays on the required one). A day shows chips, either one per status
   (Late / No actual / No input / On track / Shipped in Google's colours) or one per vendor (rows, pcs, "n late"); more than three
   fold into "n more". A row whose date has passed without an ex-factory actual is Late, whatever its steps say, and its chip
   carries "!". Clicking a day, a chip, a holiday or a week number lists those rows under the grid; a row opens the lot window.
   Public holidays come from Google's calendars through the server — Vietnam by default, the choice kept in this browser. */
const CAL_STATUS = ['late', 'noactual', 'noinput', 'ontrack', 'shipped'];
const CAL_COLOR = { late: '#D50000', noactual: '#F6BF26', noinput: '#616161', ontrack: '#0B8043', shipped: '#3F51B5' };
const VENDOR_COLORS = ['#039BE5', '#8E24AA', '#F4511E', '#33B679', '#E67C73', '#7986CB'];
const COUNTRIES = ['VN', 'KR', 'US', 'NL', 'SG', 'JP'];
const isoOf = (d) => d.toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return isoOf(d); };
const isoWeekOf = (iso) => { const t = new Date(`${iso}T00:00:00Z`); const day = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - day);
  const y = new Date(Date.UTC(t.getUTCFullYear(), 0, 1)); return Math.ceil(((t - y) / 86400000 + 1) / 7); };
const shippedLot = (l) => l.state === 'shipped' || !!l.steps?.exf?.actual;
const readCountries = () => { try { const v = JSON.parse(localStorage.getItem('psr.cal.countries')); return Array.isArray(v) && v.length ? v.filter((c) => COUNTRIES.includes(c)) : ['VN']; } catch { return ['VN']; } };

function ShipCalendar({ vendors, onOpen, refresh }) {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => isoOf(new Date(Date.now() + 7 * 3600000)), []);
  const [lots, setLots] = useState(null);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [basis, setBasis] = useState('req');
  const [view, setView] = useState('status');
  const [picked, setPicked] = useState(null);   // vendor keys ticked; null = all, until the vendor list arrives
  const [q, setQ] = useState('');
  const [countries, setCountries] = useState(readCountries);
  const [hol, setHol] = useState({ list: [], failed: [] });
  const [list, setList] = useState(null);       // { title, rows, holidays }
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/lots`, { params: { states: 'open,shipped' } }).then((r) => setLots(r.data.lots)).catch((e) => setMsg(errOf(e)));
  }, [refresh]);
  const on = picked || vendors.map((v) => v.key);
  // six weeks from the Sunday on or before the 1st
  const grid = useMemo(() => {
    const first = `${month}-01`; const start = addDays(first, -new Date(`${first}T00:00:00Z`).getUTCDay());
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [month]);
  useEffect(() => {
    try { localStorage.setItem('psr.cal.countries', JSON.stringify(countries)); } catch { /* a private window asks again */ }
    if (!countries.length) { setHol({ list: [], failed: [] }); return; }
    axios.get(`${API}/holidays`, { params: { from: grid[0], to: grid[41], countries: countries.join(',') } })
      .then((r) => setHol({ list: r.data.holidays, failed: r.data.failed || [] })).catch((e) => setMsg(errOf(e)));
  }, [countries, grid]);

  const dayOf = useCallback((l) => (basis === 'cfm' && l.cfmByVendor && l.cfmExf ? l.cfmExf : l.reqExf), [basis]);
  const statusOf = useCallback((l) => {
    if (shippedLot(l)) return 'shipped';
    if (dayOf(l) < today || l.status === 'late_vendor' || l.status === 'late_dac') return 'late';
    return { actual_missing: 'noactual', no_input: 'noinput' }[l.status] || 'ontrack';
  }, [dayOf, today]);
  // the rows the vendor ticks and the search leave
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (lots || []).filter((l) => on.includes(l.vendor) && dayOf(l)
      && (!needle || [l.lotNo, l.vendorOrderNo, l.description, l.color, l.frame, l.itemCode, ...l.pos].some((v) => String(v || '').toLowerCase().includes(needle))));
  }, [lots, on, q, dayOf]); // eslint-disable-line react-hooks/exhaustive-deps
  const byDay = useMemo(() => { const m = new Map(); rows.forEach((l) => { const d = dayOf(l); if (!m.has(d)) m.set(d, []); m.get(d).push(l); }); return m; }, [rows, dayOf]);
  const holOf = useMemo(() => { const m = new Map(); hol.list.forEach((h) => { if (!m.has(h.day)) m.set(h.day, []); m.get(h.day).push(h); }); return m; }, [hol]);
  const colorOf = (v) => VENDOR_COLORS[Math.max(0, vendors.findIndex((x) => x.key === v)) % VENDOR_COLORS.length];
  const qtyOf = (ls) => ls.reduce((a, l) => a + Number(l.reqQty || 0), 0);

  const inMonth = rows.filter((l) => dayOf(l).startsWith(month));
  const counts = CAL_STATUS.map((st) => [st, inMonth.filter((l) => statusOf(l) === st).length]);
  const past = rows.filter((l) => !shippedLot(l) && dayOf(l) < today);
  const lang = i18n.language || 'en';
  const title = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${month}-01T00:00:00Z`));
  const weekdays = grid.slice(0, 7).map((d) => new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`)));
  const step = (n) => { const d = new Date(`${month}-01T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); setMonth(isoOf(d).slice(0, 7)); setList(null); };
  const show = (titleText, ls, hs = []) => {
    setList({ title: titleText, rows: [...ls].sort((x, y) => (dayOf(x) < dayOf(y) ? -1 : dayOf(x) > dayOf(y) ? 1 : CAL_STATUS.indexOf(statusOf(x)) - CAL_STATUS.indexOf(statusOf(y)))), holidays: hs });
    setTimeout(() => document.querySelector('[data-psr-callist]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };
  const showDay = (d, ls = byDay.get(d) || []) => show(t('xPsr.calListDay', { d: dmy(d), n: ls.length, qty: qtyOf(ls).toLocaleString() }), ls, holOf.get(d) || []);
  const showWeek = (first) => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(first, i)); const ls = days.flatMap((d) => byDay.get(d) || []);
    show(t('xPsr.calListWeek', { w: isoWeekOf(addDays(first, 1)), n: ls.length, qty: qtyOf(ls).toLocaleString() }), ls, days.flatMap((d) => holOf.get(d) || []));
  };
  const chipsOf = (d) => {
    const ls = byDay.get(d) || []; const pastDay = d < today;
    if (view === 'status') {
      return CAL_STATUS.map((st) => { const g = ls.filter((l) => statusOf(l) === st); return g.length ? { key: st, rows: g, color: CAL_COLOR[st], bang: pastDay && st !== 'shipped',
        text: `${t(`xPsr.calS_${st}`)} · ${g.length}`, sub: `${qtyOf(g).toLocaleString()} pcs` } : null; }).filter(Boolean);
    }
    return vendors.filter((v) => on.includes(v.key)).map((v) => { const g = ls.filter((l) => l.vendor === v.key); if (!g.length) return null;
      const late = g.filter((l) => statusOf(l) === 'late').length;
      return { key: v.key, rows: g, color: colorOf(v.key), bang: pastDay && g.some((l) => !shippedLot(l)), text: `${v.key} · ${g.length}`, sub: `${qtyOf(g).toLocaleString()} pcs`, late }; }).filter(Boolean);
  };
  const toggle = (k) => setPicked(on.includes(k) ? on.filter((x) => x !== k) : [...on, k]);
  const toggleCountry = (c) => setCountries((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  if (!lots) return <p className="psr-muted">{msg || t('xPsr.loading')}</p>;
  return (
    <div className="psr-cal" data-psr-cal>
      <div className="psr-bar">
        <button type="button" className="sp-btn" onClick={() => { setMonth(today.slice(0, 7)); showDay(today); }} data-psr-caltoday>{t('xPsr.today')}</button>
        <button type="button" className="sp-btn" onClick={() => step(-1)} aria-label={t('xPsr.calPrev')} data-psr-calprev>‹</button>
        <button type="button" className="sp-btn" onClick={() => step(1)} aria-label={t('xPsr.calNext')} data-psr-calnext>›</button>
        <b className="psr-cal-title" data-psr-caltitle>{title}</b>
        <select value={basis} onChange={(e) => setBasis(e.target.value)} aria-label={t('xPsr.calBasis')} data-psr-calbasis>
          <option value="req">{t('xPsr.calBasis_req')}</option>
          <option value="cfm">{t('xPsr.calBasis_cfm')}</option>
        </select>
        <div className="psr-pills" role="group" aria-label={t('xPsr.calView')}>
          <button type="button" className={view === 'status' ? 'on' : ''} onClick={() => setView('status')} data-psr-calview="status">{t('xPsr.calViewStatus')}</button>
          <button type="button" className={view === 'vendor' ? 'on' : ''} onClick={() => setView('vendor')} data-psr-calview="vendor">{t('xPsr.calViewVendor')}</button>
        </div>
        <button type="button" className="sp-btn psr-cal-pastbtn" disabled={!past.length} onClick={() => show(t('xPsr.calListPast', { n: past.length, qty: qtyOf(past).toLocaleString() }), past)} data-psr-calpast>
          {t('xPsr.calPastBtn', { n: past.length })}
        </button>
      </div>
      <div className="psr-bar psr-cal-filters">
        <span className="psr-cal-group" role="group" aria-label={t('xPsr.calVendors')}>
          <b className="psr-small">{t('xPsr.calVendors')}</b>
          {vendors.map((v) => (
            <label key={v.key} className="psr-check" data-psr-calvendor={v.key}>
              <input type="checkbox" checked={on.includes(v.key)} onChange={() => toggle(v.key)} />
              <i className="psr-cal-swatch" style={{ background: colorOf(v.key) }} /> {v.key}
            </label>
          ))}
          <button type="button" className="psr-linkbtn" onClick={() => setPicked(vendors.map((v) => v.key))}>{t('xPsr.calAll')}</button>
          <button type="button" className="psr-linkbtn" onClick={() => setPicked([])} data-psr-calnone>{t('xPsr.calNone')}</button>
        </span>
        <input className="psr-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xPsr.search')} data-psr-calsearch />
        <span className="psr-cal-group" role="group" aria-label={t('xPsr.calHolidays')}>
          <b className="psr-small">{t('xPsr.calHolidays')}</b>
          {COUNTRIES.map((c) => (
            <label key={c} className="psr-check" title={t(`xPsr.calCountry_${c}`)} data-psr-calcountry={c}>
              <input type="checkbox" checked={countries.includes(c)} onChange={() => toggleCountry(c)} /> {c}
            </label>
          ))}
        </span>
      </div>
      <div className="psr-cal-legend psr-small" data-psr-calsum>
        {counts.map(([st, n]) => <span key={st} data-psr-calcount={st}><i className="psr-cal-swatch" style={{ background: CAL_COLOR[st] }} /> {t(`xPsr.calS_${st}`)} <b>{n}</b></span>)}
        <span className="psr-shown">{t('xPsr.calMonthSum', { n: inMonth.length, qty: qtyOf(inMonth).toLocaleString() })}</span>
      </div>
      {basis === 'cfm' && <p className="psr-muted psr-small">{t('xPsr.calFallback')}</p>}
      {hol.failed.length > 0 && <p className="psr-warn">{t('xPsr.calHolidayFail', { c: hol.failed.join(', ') })}</p>}
      {msg && <p className="sp-error">{msg}</p>}

      <div className="psr-cal-grid" role="grid" aria-label={title}>
        <div className="psr-cal-wd psr-cal-wk" aria-hidden="true" />
        {weekdays.map((w, i) => <div key={w} className={`psr-cal-wd ${i === 0 ? 'sun' : ''}`} role="columnheader">{w}</div>)}
        {grid.map((d, i) => {
          const chips = chipsOf(d); const hs = holOf.get(d) || []; const ls = byDay.get(d) || [];
          const cls = ['psr-cal-day', d.slice(0, 7) !== month ? 'out' : '', d === today ? 'today' : '', i % 7 === 0 ? 'sun' : '', hs.length ? 'off' : ''].filter(Boolean).join(' ');
          return (
            <React.Fragment key={d}>
              {i % 7 === 0 && (
                <button type="button" className="psr-cal-wk" onClick={() => showWeek(d)} data-psr-calweek={isoWeekOf(addDays(d, 1))} title={t('xPsr.calWeek', { w: isoWeekOf(addDays(d, 1)) })}>
                  {isoWeekOf(addDays(d, 1))}
                </button>
              )}
              <div className={cls} role="gridcell" data-psr-calday={d}>
                <button type="button" className="psr-cal-head" onClick={() => showDay(d)} aria-label={dmy(d)}>
                  <span className="psr-cal-num">{Number(d.slice(8))}</span>
                </button>
                {hs.map((h) => (
                  <button key={`${h.country}${h.name}`} type="button" className="psr-cal-hol" onClick={() => showDay(d)} title={`${t(`xPsr.calCountry_${h.country}`)} · ${h.name}`} data-psr-calhol={h.country}>
                    {h.country} · {h.name}
                  </button>
                ))}
                {chips.slice(0, chips.length > 3 ? 2 : 3).map((c) => (
                  <button key={c.key} type="button" className={`psr-cal-chip ${c.key === 'noactual' ? 'light' : ''}`} style={{ '--psr-c': c.color }}
                    onClick={() => showDay(d, c.rows)} title={`${c.text} · ${c.sub}`} data-psr-calchip={c.key}>
                    {c.bang && <b className="psr-cal-warn" aria-label={t('xPsr.calPastMark')}>!</b>}
                    <span className="psr-cal-chiptext">{c.text}</span>
                    {c.late ? <em className="psr-cal-late">{t('xPsr.calLate', { n: c.late })}</em> : null} <span className="psr-cal-chipsub">{c.sub}</span>
                  </button>
                ))}
                {chips.length > 3 && <button type="button" className="psr-cal-more" onClick={() => showDay(d)} data-psr-calmore={d}>{t('xPsr.calMore', { n: chips.length - 2 })}</button>}
                {ls.length > 0 && <span className="psr-cal-dot" aria-hidden="true">{ls.length}</span>}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {list && (
        <div className="psr-cal-daylist" data-psr-callist>
          <div className="psr-inline"><h4>{list.title}</h4><span className="mh-grow" />
            <button type="button" className="psr-x" onClick={() => setList(null)} aria-label={t('xPsr.close')}><X size={16} /></button></div>
          {list.holidays.map((h) => <p key={`${h.day}${h.country}${h.name}`} className="psr-warn">{dmy(h.day)} · {t(`xPsr.calCountry_${h.country}`)} · {h.name}</p>)}
          {!list.rows.length ? <p className="psr-muted">{t('xPsr.calEmpty')}</p> : (
            <div className="psr-scroll psr-cal-listscroll">
              <table className="sp-table psr-table">
                <thead><tr><th>{t('xPsr.colStatus')}</th><th>{t('xPsr.colVendor')}</th><th>{t('xPsr.colLot')}</th><th>{t('xPsr.colItem')}</th>
                  <th className="num">{t('xPsr.colQty')}</th><th>{basis === 'cfm' ? t('xPsr.calBasis_cfm') : t('xPsr.calBasis_req')}</th><th>{t('xPsr.colAction')}</th></tr></thead>
                <tbody>
                  {list.rows.map((l) => (
                    <tr key={l.id} data-psr-callistrow={l.id}>
                      <td><span className="psr-cal-st" style={{ '--psr-c': CAL_COLOR[statusOf(l)] }}>{t(`xPsr.calS_${statusOf(l)}`)}</span></td>
                      <td>{l.vendor}</td>
                      <td className="psr-nowrap"><b>{l.vendorOrderNo || '—'}</b><div className="psr-muted psr-small">{l.pos.join(', ')} · {l.lotNo}</div></td>
                      <td className="psr-item"><b>{l.description}</b><div className="psr-muted psr-small">{l.color}{l.frame ? ` · ${l.frame}` : ''}</div></td>
                      <td className="num">{Number(l.reqQty).toLocaleString()}</td>
                      <td className="psr-nowrap">{dmy(dayOf(l))}{basis === 'cfm' && !(l.cfmByVendor && l.cfmExf) ? <span className="psr-muted psr-small"> ({t('xPsr.calBasis_req')})</span> : null}</td>
                      <td><button type="button" className="sp-btn primary" onClick={() => onOpen(l.id)} data-psr-calopen={l.id}>{t('xPsr.open')}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductionStatus() {
  const { t } = useTranslation();
  const { external } = useAuth();
  const [meta, setMeta] = useState(null);
  const [lots, setLots] = useState(null);
  const [changes, setChanges] = useState([]);
  const [vendor, setVendor] = useState('');
  const [tab, setTab] = useState('rows');
  const [statusF, setStatusF] = useState('all');
  const [changedOnly, setChangedOnly] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [openId, setOpenId] = useState(null);
  const [calTick, setCalTick] = useState(0);
  const [bulk, setBulk] = useState({ step: 'packing', what: 'actual', date: '' });
  const [msg, setMsg] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [xl, setXl] = useState(null);           // { file, data } of an upload being previewed
  const [xlBusy, setXlBusy] = useState(false);
  const today = useMemo(() => new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 10), []);

  const load = useCallback(() => {
    axios.get(`${API}/lots`, { params: { vendor: vendor || undefined } }).then((r) => setLots(r.data.lots)).catch((e) => setMsg({ bad: true, text: errOf(e) }));
    axios.get(`${API}/changes`).then((r) => setChanges(r.data.changes)).catch(() => {});
  }, [vendor]);
  useEffect(() => { axios.get(`${API}/meta`).then((r) => setMeta(r.data)).catch((e) => setMsg({ bad: true, text: errOf(e) })); }, []);
  useEffect(() => { setLots(null); setSel(new Set()); load(); }, [load]);

  const me = meta?.me || { canEdit: false, isVendor: !!external, pt: false };
  const filtered = useMemo(() => (lots || []).filter((l) => (statusF === 'all' || l.status === statusF)
    && (!changedOnly || l.pendingChanges > 0)
    && (!q.trim() || [l.lotNo, l.vendorOrderNo, l.description, l.color, l.frame, l.itemCode, ...l.pos, ...(l.customerPos || [])].some((v) => String(v || '').toLowerCase().includes(q.trim().toLowerCase())))), [lots, statusF, changedOnly, q]);
  const columns = useMemo(() => [
    { key: 'status', get: (l) => t(`xPsr.st_${l.status}`) },
    { key: 'vendor', get: (l) => l.vendor },
    { key: 'lotNo', get: (l) => `${l.vendorOrderNo || ''} ${l.lotNo}` },
    { key: 'item', get: (l) => `${l.description || ''} · ${l.color || ''}` },
    { key: 'reqQty', get: (l) => Number(l.reqQty) },
    { key: 'reqExf', get: (l) => l.reqExf || '' },
    { key: 'cfmExf', get: (l) => (l.cfmByVendor ? l.cfmExf : '') || '' },
    { key: 'progress', get: (l) => Number(l.progress) },
    { key: 'next', get: (l) => (l.nextStep ? t(`xPsr.step_${l.nextStep}`) : '—') },
    { key: 'updated', get: (l) => l.lastUpdateAt || '' },
  ], [t]);
  const tools = useColumnTools(filtered, columns);
  const counts = useMemo(() => {
    const c = {}; (lots || []).forEach((l) => { c[l.status] = (c[l.status] || 0) + 1; }); return c;
  }, [lots]);
  const todo = useMemo(() => ({
    actual: (lots || []).filter((l) => l.status === 'actual_missing' || l.status === 'late_vendor').length,
    changes: changes.filter((c) => c.ackStatus === 'pending').length,
    cfm: (lots || []).filter((l) => l.state === 'open' && !l.cfmByVendor).length,
  }), [lots, changes]);

  const toggle = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allShown = tools.shown.length > 0 && tools.shown.every((l) => sel.has(l.id));
  const runBulk = async () => {
    const change = bulk.what === 'cfm' ? null : bulk.what === 'na' ? { steps: { [bulk.step]: { status: 'na' } } }
      : { steps: { [bulk.step]: { [bulk.what === 'actual' ? 'actual' : 'plan']: bulk.date } } };
    setMsg(null);
    try {
      let ok = 0; let failed = 0; const errs = [];
      if (bulk.what === 'cfm') {
        // each lot's own required EXF — one call per lot
        for (const id of sel) {
          const l = lots.find((x) => x.id === id);
          try { await axios.put(`${API}/lots/${id}`, { cfmExf: l.reqExf }); ok += 1; } catch (e) { failed += 1; errs.push(`${l.lotNo}: ${errOf(e)}`); } // eslint-disable-line no-await-in-loop
        }
      } else {
        const r = await axios.post(`${API}/bulk`, { ids: [...sel], change });
        ok = r.data.ok; failed = r.data.failed;
        r.data.results.filter((x) => !x.ok).slice(0, 5).forEach((x) => errs.push(`${lots.find((l) => l.id === x.id)?.lotNo}: ${x.error}`));
      }
      setMsg({ bad: failed > 0, text: `${t('xPsr.bulkDone', { ok, failed })}${errs.length ? ` ${errs.join(' · ')}` : ''}` });
      setSel(new Set()); load();
    } catch (e) { setMsg({ bad: true, text: errOf(e) }); }
  };
  const download = async () => {
    try {
      const r = await axios.post(`${API}/export`, { ids: tools.shown.map((l) => l.id), vendor: vendor || null }, { responseType: 'blob' });
      const name = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || 'PSR.xlsx';
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (e) { setMsg({ bad: true, text: errOf(e) }); }
  };
  const sendFile = async (file, apply) => {
    setXlBusy(true); setMsg(null);
    try {
      const r = await axios.post(`${API}/import`, { file, apply });
      setXl({ file, data: r.data });
      if (apply) load();
    } catch (e) { setMsg({ bad: true, text: errOf(e) }); setXl(null); } finally { setXlBusy(false); }
  };
  const pickFile = (f) => {
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => sendFile(String(rd.result).split(',')[1], false);
    rd.readAsDataURL(f);
  };
  const doSync = async () => {
    setSyncing(true); setMsg(null);
    try {
      const r = await axios.post(`${API}/sync`, {}, { timeout: 400000 });
      setMsg({ bad: false, text: t('xPsr.synced', r.data) });
      setMeta((m) => ({ ...m, lastSync: r.data })); load();
    } catch (e) { setMsg({ bad: true, text: errOf(e) }); } finally { setSyncing(false); }
  };

  return (
    <div className="pa-page">
      <Header />
      <main className="pa-main psr-page">
        <div className="mh-titlerow">
          <div className="pa-title">
            <h2>{t('xPsr.title')}{me.isVendor && meta?.vendors?.[0] ? ` · ${meta.vendors[0].name}` : ''}</h2>
            <p>{me.isVendor ? t('xPsr.subVendor') : t('xPsr.sub')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="productionStatus" ready={!!lots && !!meta} onBeforeTour={() => { setOpenId(null); setTab('rows'); }} />
          {me.pt && (
            <div className="psr-sync">
              {meta?.lastSync?.at && <span className="psr-muted">{t('xPsr.lastSync', { when: dmyTime(meta.lastSync.at) })}</span>}
              <button type="button" className="sp-btn" disabled={syncing} onClick={doSync} data-psr-sync><RefreshCw size={13} /> {syncing ? t('xPsr.syncing') : t('xPsr.sync')}</button>
            </div>
          )}
        </div>

        {me.isVendor && lots && (
          <div className="psr-todo" data-psr-todo>
            <b><CalendarClock size={15} /> {t('xPsr.todoTitle')}</b>
            <button type="button" onClick={() => { setTab('rows'); setStatusF('actual_missing'); }}>{t('xPsr.todoActual', { n: todo.actual })}</button>
            <button type="button" onClick={() => setTab('changes')}>{t('xPsr.todoChanges', { n: todo.changes })}</button>
            <span>{t('xPsr.todoCfm', { n: todo.cfm })}</span>
          </div>
        )}

        <div className="pa-tabs view-tabs" role="tablist">
          <button type="button" className={`pa-tab ${tab === 'rows' ? 'active' : ''}`} onClick={() => setTab('rows')} data-psr-tab="rows"><ListChecks size={15} strokeWidth={2.2} /> {t('xPsr.tabRows')}</button>
          {!me.isVendor && <button type="button" className={`pa-tab ${tab === 'cal' ? 'active' : ''}`} onClick={() => setTab('cal')} data-psr-tab="cal"><CalendarDays size={15} strokeWidth={2.2} /> {t('xPsr.tabCal')}</button>}
          <button type="button" className={`pa-tab ${tab === 'changes' ? 'active' : ''}`} onClick={() => setTab('changes')} data-psr-tab="changes">
            <BellRing size={15} strokeWidth={2.2} /> {t('xPsr.tabChanges')}{changes.length ? <em className="psr-count">{changes.length}</em> : null}
          </button>
        </div>
        {msg && <p className={msg.bad ? 'sp-error' : 'sp-ok'} role="status" data-psr-msg>{msg.text}</p>}
        {!me.canEdit && meta && !me.isVendor && <p className="sp-note">{t('xPsr.readOnly')}</p>}

        {tab === 'rows' && (
          <>
            <div className="psr-bar">
              {!me.isVendor && (
                <select value={vendor} onChange={(e) => setVendor(e.target.value)} aria-label={t('xPsr.vendor')} data-psr-vendor>
                  <option value="">{t('xPsr.allVendors')}</option>
                  {(meta?.vendors || []).map((v) => <option key={v.key} value={v.key}>{v.key} · {v.name}</option>)}
                </select>
              )}
              <input className="psr-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xPsr.search')} data-psr-search />
              <div className="psr-pills" role="group">
                <button type="button" className={statusF === 'all' ? 'on' : ''} onClick={() => setStatusF('all')}>{t('xPsr.all')} <em>{(lots || []).length}</em></button>
                {STATUS_ORDER.filter((s) => counts[s]).map((s) => (
                  <button key={s} type="button" className={`${statusF === s ? 'on' : ''} ${s}`} onClick={() => setStatusF(s)} data-psr-pill={s}>{t(`xPsr.st_${s}`)} <em>{counts[s]}</em></button>
                ))}
              </div>
              <label className="psr-check"><input type="checkbox" checked={changedOnly} onChange={(e) => setChangedOnly(e.target.checked)} /> {t('xPsr.changedOnly')}</label>
              {tools.active && <button type="button" className="sp-btn" onClick={tools.clear}>{t('xPsr.clearFilters')}</button>}
              <button type="button" className="sp-btn" disabled={!lots || !tools.shown.length} onClick={download} data-psr-xldown><Download size={13} /> {t('xPsr.xlDown')}</button>
              {me.canEdit && (
                <label className="sp-btn psr-upload" data-psr-xlup>
                  <Upload size={13} /> {xlBusy ? t('xPsr.xlReading') : t('xPsr.xlUp')}
                  <input type="file" accept=".xlsx" hidden onChange={(e) => { pickFile(e.target.files[0]); e.target.value = ''; }} data-psr-xlfile />
                </label>
              )}
              <span className="psr-shown">{lots ? t('xPsr.shownOf', { n: tools.shown.length, m: lots.length }) : t('xPsr.loading')}</span>
            </div>

            {me.canEdit && sel.size > 0 && (
              <div className="psr-bulk" data-psr-bulk>
                <b>{t('xPsr.selected', { n: sel.size })}</b>
                <select value={bulk.what} onChange={(e) => setBulk((b) => ({ ...b, what: e.target.value }))} aria-label={t('xPsr.bulkWhat')} data-psr-bulkwhat>
                  <option value="actual">{t('xPsr.bulkActual')}</option>
                  <option value="plan">{t('xPsr.bulkPlan')}</option>
                  <option value="na">{t('xPsr.bulkNa')}</option>
                  <option value="cfm">{t('xPsr.bulkCfmReq')}</option>
                </select>
                {bulk.what !== 'cfm' && (
                  <select value={bulk.step} onChange={(e) => setBulk((b) => ({ ...b, step: e.target.value }))} aria-label={t('xPsr.bulkStep')} data-psr-bulkstep>
                    {(meta?.steps || []).filter((s) => !(me.isVendor && s.key === 'frame')).map((s) => <option key={s.key} value={s.key}>{t(`xPsr.step_${s.key}`)}</option>)}
                  </select>
                )}
                {(bulk.what === 'actual' || bulk.what === 'plan') && (
                  <>
                    <input type="date" value={bulk.date} max={bulk.what === 'actual' ? today : undefined} onChange={(e) => setBulk((b) => ({ ...b, date: e.target.value }))} data-psr-bulkdate />
                    <button type="button" className="sp-btn" onClick={() => setBulk((b) => ({ ...b, date: today }))}>{t('xPsr.today')}</button>
                  </>
                )}
                <button type="button" className="sp-btn primary" disabled={(bulk.what === 'actual' || bulk.what === 'plan') && !bulk.date} onClick={runBulk} data-psr-bulkapply>{t('xPsr.apply')}</button>
              </div>
            )}

            <div className="psr-scroll">
              <table className="sp-table psr-table">
                <thead><tr>
                  {me.canEdit && <th className="psr-sel"><input type="checkbox" checked={allShown} aria-label="all" onChange={() => setSel(allShown ? new Set() : new Set(tools.shown.map((l) => l.id)))} /></th>}
                  <SortTh label={t('xPsr.colStatus')} {...tools.thProps('status')} />
                  {!me.isVendor && <SortTh label={t('xPsr.colVendor')} {...tools.thProps('vendor')} />}
                  <SortTh label={t('xPsr.colLot')} {...tools.thProps('lotNo')} />
                  <SortTh label={t('xPsr.colItem')} {...tools.thProps('item')} />
                  <SortTh label={t('xPsr.colQty')} className="num" {...tools.thProps('reqQty')} />
                  <SortTh label={t('xPsr.colReq')} {...tools.thProps('reqExf')} />
                  <SortTh label={t('xPsr.colCfm')} {...tools.thProps('cfmExf')} />
                  <SortTh label={t('xPsr.colProgress')} {...tools.thProps('progress')} />
                  <SortTh label={t('xPsr.colNext')} {...tools.thProps('next')} />
                  <SortTh label={t('xPsr.colUpdated')} {...tools.thProps('updated')} />
                  <th>{t('xPsr.colAction')}</th>
                </tr></thead>
                <tbody>
                  {lots && !tools.shown.length && <tr><td colSpan={12} className="psr-muted">{t('xPsr.noRows')}</td></tr>}
                  {tools.shown.map((l) => (
                    <tr key={l.id} className={sel.has(l.id) ? 'sel' : ''} data-psr-row={l.id}>
                      {me.canEdit && <td className="psr-sel"><input type="checkbox" checked={sel.has(l.id)} onChange={() => toggle(l.id)} aria-label={l.lotNo} /></td>}
                      <td><Chip status={l.status} />{l.pendingChanges > 0 && <span className="psr-flag" title={t('xPsr.changedOnly')}><BellRing size={12} /> {l.pendingChanges}</span>}</td>
                      {!me.isVendor && <td>{l.vendor}</td>}
                      <td className="psr-nowrap"><b>{l.vendorOrderNo || '—'}</b><div className="psr-muted psr-small">{l.pos.join(', ')} · {l.lotNo}</div></td>
                      <td className="psr-item"><b>{l.description}</b><div className="psr-muted psr-small">{l.color}{l.frame ? ` · ${l.frame}` : ''}{l.customerPos?.length ? ` · ${l.customerPos.slice(0, 3).join(', ')}${l.customerPos.length > 3 ? '…' : ''}` : ''}</div></td>
                      <td className="num">{Number(l.reqQty).toLocaleString()}</td>
                      <td className="psr-nowrap">{dmy(l.reqExf)}</td>
                      <td className="psr-nowrap">{l.cfmByVendor ? dmy(l.cfmExf) : <span className="psr-muted">—</span>}</td>
                      <td><div className="psr-bar-pct" title={`${l.progress}%`}><span style={{ width: `${l.progress}%` }} /></div><span className="psr-small">{l.progress}%</span></td>
                      <td className="psr-nowrap">{l.nextStep ? t(`xPsr.step_${l.nextStep}`) : '—'}</td>
                      <td className="psr-nowrap psr-small">{l.lastUpdateAt ? dmyTime(l.lastUpdateAt) : '—'}</td>
                      <td><button type="button" className="sp-btn primary" onClick={() => setOpenId(l.id)} data-psr-open={l.id}>{t('xPsr.open')}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'cal' && !me.isVendor && <ShipCalendar vendors={meta?.vendors || []} onOpen={setOpenId} refresh={calTick} />}
        {tab === 'changes' && (
          <div className="psr-changes" data-psr-changes>
            {!changes.length ? <p className="psr-muted">{t('xPsr.noChanges')}</p> : (
              <table className="sp-table">
                <thead><tr>{!me.isVendor && <th>{t('xPsr.colVendor')}</th>}<th>{t('xPsr.colLot')}</th><th>{t('xPsr.colItem')}</th><th>{t('xPsr.history')}</th><th>{t('xPsr.colStatus')}</th><th>{t('xPsr.colAction')}</th></tr></thead>
                <tbody>
                  {changes.map((c) => (
                    <tr key={c.id} data-psr-change={c.id}>
                      {!me.isVendor && <td>{c.vendor}</td>}
                      <td className="psr-nowrap"><b>{c.vendorOrderNo || '—'}</b><div className="psr-muted psr-small">{c.lotNo}</div></td>
                      <td>{c.description} <span className="psr-muted">· {c.color}</span></td>
                      <td>{psrField(t, c.field)}: {psrValue(t, c.oldValue)} → <b>{psrValue(t, c.newValue)}</b>
                        <div className="psr-muted psr-small">{dmyTime(c.createdAt)}{c.afterClose ? ` · ${t('xPsr.afterClose')}` : ''}{c.reason ? ` — ${c.reason}` : ''}</div></td>
                      <td className="psr-small">{c.ackStatus === 'suggested' ? t('xPsr.chgWaitPt', { d: dmy(c.suggestValue) }) : c.ackStatus === 'pending' ? t('xPsr.chgWaitVendor') : t('xPsr.afterClose')}</td>
                      <td><button type="button" className="sp-btn primary" onClick={() => setOpenId(c.lotId)}>{t('xPsr.open')}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {xl && <ImportPreview data={xl.data} busy={xlBusy} onApply={() => sendFile(xl.file, true)} onClose={() => setXl(null)} />}
        {openId && meta && <LotDialog id={openId} me={me} steps={meta.steps} onClose={() => setOpenId(null)} onSaved={() => { load(); setCalTick((n) => n + 1); }} />}
      </main>
    </div>
  );
}
